import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from "@codemirror/commands";
import { python } from "@codemirror/lang-python";
import { sql } from "@codemirror/lang-sql";
import {
  HighlightStyle,
  bracketMatching,
  indentOnInput,
  syntaxHighlighting,
} from "@codemirror/language";
import { type Extension } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import { tags } from "@lezer/highlight";

import type { CodeLanguage } from "./code-language";

const lightHighlight = HighlightStyle.define([
  { tag: tags.keyword, color: "#d73a49" },
  { tag: tags.controlKeyword, color: "#d73a49" },
  { tag: tags.string, color: "#032f62" },
  { tag: tags.comment, color: "#6a737d", fontStyle: "italic" },
  { tag: tags.number, color: "#005cc5" },
  { tag: tags.operator, color: "#d73a49" },
  { tag: tags.typeName, color: "#6f42c1" },
  { tag: tags.variableName, color: "#24292e" },
  { tag: tags.function(tags.variableName), color: "#6f42c1" },
  { tag: tags.special(tags.variableName), color: "#005cc5" },
]);

const darkHighlight = HighlightStyle.define([
  { tag: tags.keyword, color: "#569cd6" },
  { tag: tags.controlKeyword, color: "#c586c0" },
  { tag: tags.operatorKeyword, color: "#569cd6" },
  { tag: tags.atom, color: "#569cd6" },
  { tag: tags.bool, color: "#569cd6" },
  { tag: tags.null, color: "#569cd6" },
  { tag: tags.string, color: "#ce9178" },
  { tag: tags.comment, color: "#6a9955", fontStyle: "italic" },
  { tag: tags.number, color: "#b5cea8" },
  { tag: tags.operator, color: "#d4d4d4" },
  { tag: tags.punctuation, color: "#d4d4d4" },
  { tag: tags.typeName, color: "#4ec9b0" },
  { tag: tags.className, color: "#4ec9b0" },
  { tag: tags.namespace, color: "#4ec9b0" },
  { tag: tags.standard(tags.name), color: "#4ec9b0" },
  { tag: tags.function(tags.variableName), color: "#dcdcaa" },
  { tag: tags.function(tags.propertyName), color: "#dcdcaa" },
  { tag: tags.variableName, color: "#9cdcfe" },
  { tag: tags.special(tags.variableName), color: "#9cdcfe" },
  { tag: tags.propertyName, color: "#9cdcfe" },
]);

export function languageExtension(language: CodeLanguage): Extension {
  return language === "sql" ? sql() : python();
}

export function themeExtension(
  theme: "light" | "dark",
  readOnly: boolean,
): Extension {
  const highlight = theme === "light" ? lightHighlight : darkHighlight;
  const caret = theme === "dark" ? "#fff" : "#000";
  const foreground = theme === "dark" ? "#e6edf3" : "#24292e";
  const editorTheme = {
    "&": {
      color: foreground,
      backgroundColor: "transparent",
      height: "100%",
      fontSize: "0.875rem",
    },
    ".cm-scroller": {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      overflow: "auto",
    },
    ".cm-content": {
      color: foreground,
      caretColor: readOnly ? "transparent" : caret,
      whiteSpace: "pre",
    },
    ".cm-line": {
      whiteSpace: "pre",
    },
    ".cm-format-error-line": {
      backgroundColor: "color-mix(in srgb, #dc2626 18%, transparent)",
      boxShadow: "inset 3px 0 #dc2626",
    },
    ".cm-format-error-range": {
      backgroundColor: "color-mix(in srgb, #dc2626 32%, transparent)",
      borderRadius: "2px",
    },
    "&.cm-focused": {
      outline: "none",
    },
    ...(readOnly ? { ".cm-cursor": { display: "none" } } : {}),
  };

  return [
    syntaxHighlighting(highlight),
    EditorView.theme(editorTheme, { dark: theme === "dark" }),
  ];
}

export function editingKeymap(): Extension {
  return keymap.of([...historyKeymap, ...defaultKeymap, indentWithTab]);
}

export function coreExtensions(): Extension {
  return [history(), indentOnInput(), bracketMatching(), editingKeymap()];
}
